import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { getPoolStats, type PoolTeamStats } from "@/hooks/matches";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { formatOrdinals } from "@/lib/numbers";
import { invalidateMatchQueries } from "@/lib/tournament-cache";
import { isNotNullOrUndefined } from "@/utils/types";

import { TeamRadioList } from "./team-radio-list";

export type WildcardOpponent = {
  finish: number | null;
  poolName: string;
} | null;

export type AssignWildcardFormProps = {
  tournamentId: number;
  tournamentDivisionId: number;
  matchId: number;
  opponent?: WildcardOpponent;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const teamIdSchema = z.object({
  teamId: z.number(),
});

export function AssignWildcardForm({
  tournamentId,
  tournamentDivisionId,
  matchId,
  opponent,
  onOpenChange,
  ...props
}: AssignWildcardFormProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const playoffTeamIdsOptions = trpc.tournaments.getPlayoffTeamIds.queryOptions(
    {
      tournamentDivisionId,
    },
  );
  const teamsOptions = trpc.tournaments.getTeams.queryOptions({
    tournamentDivisionId,
  });
  const poolsOptions = trpc.tournaments.getPools.queryOptions({
    tournamentDivisionId,
  });

  const { data: division } = useQuery({
    ...trpc.tournaments.get.queryOptions({ id: tournamentId }),
    select: (data) =>
      data?.tournamentDivisions.find(({ id }) => id === tournamentDivisionId),
  });

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.assignWildcard.mutationOptions(),
    onSuccess: () => {
      invalidateMatchQueries(queryClient, trpc);
      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {
      teamId: null as unknown as number,
    },
    validators: {
      onMount: teamIdSchema,
      onChange: teamIdSchema,
    },
    onSubmit: ({ value: { teamId } }) => {
      mutate({
        matchId,
        teamId,
      });
    },
  });

  const { data: playoffTeamIds } = useQuery(playoffTeamIdsOptions);

  const playoffTeamSet = playoffTeamIds ? new Set(playoffTeamIds) : undefined;

  const { data: teams } = useQuery({
    ...teamsOptions,
    select: (data) =>
      data.filter(
        (team) =>
          !playoffTeamSet?.has(team.id) &&
          team.status === "confirmed" &&
          team.poolTeam?.finish,
      ),
  });

  const { data: stats } = useQuery({
    ...poolsOptions,
    select: (data) => {
      const stats = new Map(
        data
          .map((pool) => {
            const stats = getPoolStats(pool);

            return stats ? { pool: pool.name, stats } : null;
          })
          .filter(isNotNullOrUndefined)
          .flatMap(({ stats }) =>
            Object.keys(stats).map(
              (teamId) =>
                [
                  Number.parseInt(teamId, 10),
                  stats[teamId as unknown as number],
                ] as [number, PoolTeamStats],
            ),
          ),
      );

      return stats;
    },
  });

  const orderedTeams = orderBy(
    teams,
    [
      (team) => team.poolTeam?.finish,
      (team) => {
        const teamStats = stats?.get(team.id);

        if (teamStats) {
          return (
            teamStats.wins.size / (teamStats.wins.size + teamStats.losses.size)
          );
        }

        return 0;
      },
      (team) => stats?.get(team.id)?.totalPointDiff,
      (team) => team.poolTeam?.pool.name,
    ],
    ["asc", "desc", "desc", "asc"],
  );

  const divisionDisplay = division
    ? getTournamentDivisionDisplay(division)
    : "";

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Assign Wildcard
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Assign wildcard{" "}
          {opponent && opponent.finish !== null ? (
            <>
              to play against{" "}
              <span className="font-semibold italic">
                {formatOrdinals(opponent.finish)}{" "}
              </span>{" "}
              from{" "}
              <span className="font-semibold italic">
                Pool {opponent.poolName.toUpperCase()}{" "}
              </span>
              in the
            </>
          ) : (
            <>for the</>
          )}{" "}
          <span className="font-bold italic">{divisionDisplay}</span> division.
        </p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to assign wildcard"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="teamId"
            children={(field) => (
              <TeamRadioList
                teams={orderedTeams}
                stats={stats}
                onChange={(teamId) => field.handleChange(teamId)}
              />
            )}
          />

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton>Submit</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
